import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { brandify } from "../lib/brand";

const TIER_TONE = {
  "Applicant":"bg-slate-100 text-slate-700 border-slate-200",
  "StaffHub Qualified":"bg-emerald-50 text-emerald-700 border-emerald-200",
  "StaffHub Premier":"bg-amber-50 text-amber-800 border-amber-300",
};

export default function Qualification() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("");

  const load = async () => {
    const { data } = await api.get("/candidates", {params: {limit: 500}});
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const qualify = async (id, t, notes) => {
    try {
      await api.post(`/candidates/${id}/qualify`, {tier: t, notes});
      toast.success(`Set to ${t}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = rows.filter(r => {
    if (q && !`${r.first_name} ${r.last_name} ${r.primary_role || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (tier && (r.qualification_tier || "Applicant") !== tier) return false;
    return true;
  });

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Qualification</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Talent Qualification Board</h1>
        <p className="text-sm text-slate-500 mt-1">Applicant → ConnectMyHub Qualified → ConnectMyHub Premier. Premier requires human recruiter approval; AI never grants it autonomously.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex gap-3 items-center flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input data-testid="qual-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search name or role…" className="w-full pl-9 pr-3 h-9 rounded-md border border-slate-200 text-sm bg-white outline-none focus:border-sky-500"/>
          </div>
          <select data-testid="qual-tier" value={tier} onChange={e=>setTier(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white">
            <option value="">All tiers</option>
            {Object.keys(TIER_TONE).map(t=><option key={t}>{t}</option>)}
          </select>
          <div className="text-xs text-slate-500 ml-auto">{filtered.length} candidates</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest font-semibold">
            <tr><th className="text-left px-4 py-3">Candidate</th><th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Score</th><th className="text-left px-4 py-3">Video</th><th className="text-left px-4 py-3">Current tier</th><th className="text-right px-4 py-3">Set tier</th></tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} data-testid={`qual-row-${c.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">{c.first_name} {c.last_name} <span className="text-[10px] text-slate-500 ml-1">{c.country}</span></td>
                <td className="px-4 py-3 text-slate-700">{c.primary_role}{c.seniority ? ` · ${c.seniority}` : ""}</td>
                <td className="px-4 py-3 font-mono">{c.overall_staffhub_score?.toFixed(1) || "—"}</td>
                <td className="px-4 py-3">{c.intro_video_url ? <span className="text-[10px] text-emerald-700">Submitted</span> : <span className="text-[10px] text-slate-400">None</span>}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${TIER_TONE[c.qualification_tier || "Applicant"]}`}>{c.qualification_tier || "Applicant"}</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`set-applicant-${c.id}`} onClick={()=>qualify(c.id, "Applicant")} className="text-[10px] px-2 py-0.5 border border-slate-300 rounded font-semibold hover:bg-slate-50">Applicant</button>
                    <button data-testid={`set-qualified-${c.id}`} onClick={()=>qualify(c.id, "StaffHub Qualified")} className="text-[10px] px-2 py-0.5 border border-emerald-300 text-emerald-700 rounded font-semibold hover:bg-emerald-50">Qualified</button>
                    <button data-testid={`set-premier-${c.id}`} onClick={()=>qualify(c.id, "StaffHub Premier", "Human-approved Premier tier.")} className="text-[10px] px-2 py-0.5 border border-amber-300 text-amber-800 rounded font-semibold hover:bg-amber-50">Premier</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
